import { Injectable } from '@angular/core';
import {
    ImtSemantics,
    MibiStructReport,
    PatientRecordBundle,
    Report,
    ReportAckData,
    ReportCenterFilter,
    ReportCenterMasterData,
    ReportCenterRecord,
    ReportCenterRecordFilter,
    ReportCenterRecordResult,
    ReportCenterSearchResult,
    ReportMibiStructParameter,
    Ward,
    WardGroup,
} from '@delab/ngx-gen-ts-ixserv/models';
import {
    DelabNgxRecordsServiceDefinition,
    DelabNgxStructMibiServiceDefinition,
    filterMarkTypeBySemantic,
    hasRecordMarkTypes,
    RecordTypeOption,
} from '@delab/ngx-ixconnect-components';
import { map, Observable, of } from 'rxjs';
import { WardGroupsApiService } from 'src/.api-client/services/ward-groups-api.service';
import { WardsApiService } from 'src/.api-client/services/wards-api.service';
import { v4 as uuidv4 } from 'uuid';
import { RecordApiService } from '../../.api-client/services/record-api.service';
import { ReportCenterApiService } from '../../.api-client/services/report-center-api.service';
import { ReportsApiService } from '../../.api-client/services/reports-api.service';

@Injectable({ providedIn: 'root' })
export class RecordsService implements DelabNgxRecordsServiceDefinition, DelabNgxStructMibiServiceDefinition {
    #wards?: Ward[];
    #wardGroups?: WardGroup[];
    #masterData?: ReportCenterMasterData;

    constructor(
        private readonly _reportCenterApiService: ReportCenterApiService,
        private readonly _recordApiService: RecordApiService,
        private readonly _wardsApiService: WardsApiService,
        private readonly _wardGroupsApiService: WardGroupsApiService,
        private readonly _reportsApiService: ReportsApiService,
    ) {}

    public getStructuredMibiReports(reportMibiStructParameter: ReportMibiStructParameter): Observable<MibiStructReport> {
        if (!reportMibiStructParameter.casID) {
            throw Error();
        }

        return this._recordApiService.recordStructMibi({ 'x-trace-id': uuidv4(), casID: reportMibiStructParameter.casID }).pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return new MibiStructReport(value);
            }),
        );
    }

    public getRecords(reportCenterRecordFilter: ReportCenterRecordFilter): Observable<ReportCenterRecordResult> {
        return this._reportCenterApiService.reportCenterRecords({ 'x-trace-id': uuidv4(), body: reportCenterRecordFilter.toJSON() }).pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return new ReportCenterRecordResult(value);
            }),
        );
    }

    public getRecordTypes(): Observable<RecordTypeOption[] | undefined> {
        return this.getReportRecordTypes();
    }

    protected getReportRecordTypes(): Observable<RecordTypeOption[] | undefined> {
        return this._reportsApiService.reportsGetTypes().pipe(
            map((reportTypes) => {
                return reportTypes.reduce<RecordTypeOption[]>((recordTypeOptions, reportType) => {
                    const { prtID: prtId, prtName: label } = reportType;

                    if (!label) {
                        return recordTypeOptions;
                    }

                    return [...recordTypeOptions, { prtId, label }];
                }, []);
            }),
        );
    }

    public getGroups(reportCenterFilter: ReportCenterFilter): Observable<ReportCenterSearchResult> {
        return this._reportCenterApiService.reportCenterGetGroups({ 'x-trace-id': uuidv4(), body: reportCenterFilter.toJSON() }).pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return new ReportCenterSearchResult(value);
            }),
        );
    }

    public getMasterData() {
        return this.#getMasterData(false);
    }

    public getWards() {
        return this.#getWards(false);
    }

    public isRecordRead(record: ReportCenterRecord): Observable<boolean> {
        return (this.#masterData ? of(this.#masterData) : this.getMasterData()).pipe(
            map((masterData) => filterMarkTypeBySemantic(masterData?.markTypes, ImtSemantics.SEMANTICS_READ)),
            map((markTypes) => hasRecordMarkTypes(record, markTypes)),
        );
    }

    public getPatientRecordBundle(recID: number): Observable<PatientRecordBundle> {
        return this._recordApiService.recordRecordBundle({ 'x-trace-id': uuidv4(), recID }).pipe(
            map((bundle) => {
                if (typeof bundle === 'undefined') {
                    throw Error();
                }
                return new PatientRecordBundle(bundle);
            }),
        );
    }

    public getWardGroups(): Observable<WardGroup[]> {
        return this.#getWardGroups(false);
    }

    public getReportIdsByPocID(pocId: number): Observable<number[]> {
        return this._reportsApiService.reportsListReportsByPocId({ pocId: pocId, 'x-trace-id': uuidv4() });
    }

    public getReport(rmhIds: number[]): Observable<Report | undefined> {
        return this._reportsApiService
            .reportsGetReports({ body: rmhIds, 'x-trace-id': uuidv4() })
            .pipe(map((reportDto) => new Report(reportDto)));
    }

    public ackReport(report: ReportAckData) {
        return this._reportsApiService.reportsAckReport({ body: report.toJSON(), 'x-trace-id': uuidv4() });
    }

    #getWards(force: boolean = false): Observable<Ward[]> {
        if (this.#wards && !force) {
            return of(this.#wards);
        }
        return this._wardsApiService.wardsGetWards({ 'x-trace-id': uuidv4() }).pipe(
            map((wards) => {
                return (this.#wards = wards.map((ward) => new Ward(ward)));
            }),
        );
    }

    #getWardGroups(force: boolean = false): Observable<WardGroup[]> {
        if (this.#wardGroups && !force) {
            return of(this.#wardGroups);
        }
        return this._wardGroupsApiService
            .wardGroupsGetWardGroups({ 'x-trace-id': uuidv4() })
            .pipe(map((wardGroupDtos) => wardGroupDtos.map((wardGroupDto) => new WardGroup(wardGroupDto))));
    }

    #getMasterData(force: boolean = false): Observable<ReportCenterMasterData | undefined> {
        if (this.#masterData && !force) {
            return of(this.#masterData);
        }

        return this._reportCenterApiService.reportCenterGetMasterData().pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return (this.#masterData = new ReportCenterMasterData(value));
            }),
        );
    }

    getReports(rmhIDs: number[]): Observable<Report | undefined> {
        return this._reportsApiService.reportsGetReports({ 'x-trace-id': uuidv4(), body: rmhIDs }).pipe(
            map((value) => {
                return new Report(value);
            }),
        );
    }
}
